/**
 * Terms of Service (P20250925-103)
 *
 * SEO-ready, indexable, with ToC and Last Updated stamp.
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function LegalTerms() {
  const lastUpdated = '2025-09-30';

  return (
    <>
      <Helmet>
        <title>Terms of Service - Attain Domains</title>
        <meta
          name="description"
          content="Attain Domains Terms of Service: Domain listings, purchasing via our secure payment provider, no warranties, limitation of liability, governing law."
        />
        <link rel="canonical" href="https://attaindomains.com/legal/terms" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl font-bold mb-2 text-center">Terms of Service</h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            Last updated: {lastUpdated}
          </p>

          {/* Table of Contents */}
          <nav className="bg-muted/30 rounded-lg p-6 mb-8" aria-label="Table of contents">
            <h2 className="text-lg font-semibold mb-3">Contents</h2>
            <ul className="space-y-1 text-sm">
              <li>
                <a href="#scope" className="text-primary hover:underline">
                  1. Scope of Service
                </a>
              </li>
              <li>
                <a href="#purchasing" className="text-primary hover:underline">
                  2. Purchasing via our secure payment provider
                </a>
              </li>
              <li>
                <a href="#leasing" className="text-primary hover:underline">
                  3. Leasing (Beta)
                </a>
              </li>
              <li>
                <a href="#warranties" className="text-primary hover:underline">
                  4. No Warranties
                </a>
              </li>
              <li>
                <a href="#liability" className="text-primary hover:underline">
                  5. Limitation of Liability
                </a>
              </li>
              <li>
                <a href="#governing-law" className="text-primary hover:underline">
                  6. Governing Law
                </a>
              </li>
              <li>
                <a href="#changes" className="text-primary hover:underline">
                  7. Changes to Terms
                </a>
              </li>
              <li>
                <a href="#contact" className="text-primary hover:underline">
                  8. Contact
                </a>
              </li>
            </ul>
          </nav>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* Scope */}
            <section
              id="scope"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">1. Scope of Service</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Attain Domains is a <strong>domain marketplace</strong>. We list premium domains
                  for sale or lease. We are <strong>not</strong>:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    A domain registrar (we facilitate transfers; you manage renewal at your
                    registrar)
                  </li>
                  <li>Legal or financial advisors (consult a professional before purchasing)</li>
                  <li>
                    Trademark counsel (you are responsible for ensuring your use does not infringe
                    third-party rights)
                  </li>
                </ul>
                <p>
                  By accessing or using Attain Domains, you agree to these Terms. If you do not
                  agree, please do not use the service.
                </p>
              </div>
            </section>

            {/* Purchasing */}
            <section
              id="purchasing"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                2. Purchasing via our secure payment provider
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  All Buy-It-Now (BIN) purchases are processed via{' '}
                  <strong>our trusted payment provider</strong>:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Payment:</strong> Buyer pays our trusted payment provider (funds held in
                    escrow).
                  </li>
                  <li>
                    <strong>Transfer:</strong> Seller initiates domain transfer to buyer's
                    registrar.
                  </li>
                  <li>
                    <strong>Inspection:</strong> Buyer verifies receipt (typically 5-7 business
                    days).
                  </li>
                  <li>
                    <strong>Release:</strong> our trusted payment provider releases funds to seller
                    upon buyer approval.
                  </li>
                </ul>
                <p>
                  <strong>Escrow fees:</strong> Buyer pays Escrow fees (typically 3.25% or $25
                  minimum, varies by transaction size). See{' '}
                  <Link to="/legal/escrow-and-refunds" className="text-primary hover:underline">
                    Escrow & Refunds
                  </Link>{' '}
                  for details.
                </p>
                <p>
                  <strong>Final sale:</strong> Once Escrow releases funds, sales are final (no
                  refunds except Escrow disputes).
                </p>
              </div>
            </section>

            {/* Leasing */}
            <section
              id="leasing"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">3. Leasing (Beta)</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Some domains may be available for <strong>Lease-to-Own</strong>:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Monthly payments:</strong> Spread cost over 12-36 months.
                  </li>
                  <li>
                    <strong>Early cancellation:</strong> Request cancellation via your Account page.
                    Refunds are prorated (see{' '}
                    <Link
                      to="/legal/escrow-and-refunds#leasing"
                      className="text-primary hover:underline"
                    >
                      Refunds
                    </Link>
                    ).
                  </li>
                  <li>
                    <strong>Transfer:</strong> Domain transfers to your ownership after final
                    payment.
                  </li>
                </ul>
                <p>
                  Leasing is currently in beta. Terms may change; existing leases are grandfathered.
                </p>
              </div>
            </section>

            {/* Warranties */}
            <section
              id="warranties"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">4. No Warranties</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Domains are sold "as is."</strong> We provide:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Clean title:</strong> No known liens or legal encumbrances.
                  </li>
                  <li>
                    <strong>Unlocked:</strong> Domain is ready for transfer.
                  </li>
                </ul>
                <p>
                  We do <strong>not</strong> warrant:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>SEO value, traffic, backlinks, or search rankings.</li>
                  <li>Freedom from trademark disputes (buyer assumes all IP risk).</li>
                  <li>Specific use cases or business outcomes.</li>
                </ul>
              </div>
            </section>

            {/* Liability */}
            <section
              id="liability"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                5. Limitation of Liability
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Maximum liability:</strong> Attain Domains' total liability is limited to
                  the amount you paid for the domain (or 12 months of lease payments, whichever is
                  less).
                </p>
                <p>
                  <strong>No consequential damages:</strong> We are not liable for indirect,
                  incidental, special, consequential, or punitive damages, including:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Loss of profits, revenue, goodwill, or business opportunities.</li>
                  <li>Data loss or corruption.</li>
                  <li>Third-party claims (e.g., trademark disputes).</li>
                </ul>
                <p>
                  <strong>Escrow disputes:</strong> Disputes related to payment/transfer are handled
                  by our trusted payment provider per their terms.
                </p>
              </div>
            </section>

            {/* Governing Law */}
            <section
              id="governing-law"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">6. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms are governed by the laws of <strong>[Your Jurisdiction]</strong>,
                without regard to conflict of law principles. Any disputes shall be resolved in the
                courts of [Your Jurisdiction].
              </p>
            </section>

            {/* Changes */}
            <section
              id="changes"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">7. Changes to Terms</h2>
              <p className="text-muted-foreground">
                We may update these Terms from time to time. Changes are effective immediately upon
                posting. Continued use of Attain Domains after changes constitutes acceptance of the
                new Terms. Material changes will be announced via email (if you have an account) and
                a banner on the site.
              </p>
            </section>

            {/* Contact */}
            <section
              id="contact"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">8. Contact</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>Questions about these Terms?</p>
                <p>
                  <strong>Email:</strong> legal@attaindomains.com
                </p>
                <p>
                  <strong>Support:</strong>{' '}
                  <Link to="/support/contact" className="text-primary hover:underline">
                    Contact form
                  </Link>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
